import React, { useState } from 'react';
import { Link } from 'wouter';
import { Navbar } from '@/components/Navbar';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { CheckCircle, Star, Users, BookOpen, Video, CreditCard, Clock, Shield } from 'lucide-react';

const CheckoutForm = () => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [email, setEmail] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);

    try {
      const response = await apiRequest("POST", "/api/create-checkout-session", {
        email,
        priceId: "mentorship_annual_promo",
        paymentMethod: "card"
      });

      const data = await response.json();
      
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error(data.message || "Failed to create checkout session");
      }
    } catch (error: any) {
      toast({
        title: "Payment Failed",
        description: error.message || "Failed to initialize payment",
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
          Email Address
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="your@email.com"
        />
      </div>
      <Button 
        type="submit" 
        disabled={isProcessing}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg font-semibold"
      >
        <CreditCard className="h-5 w-5 mr-2" />
        {isProcessing ? 'Setting up payment...' : 'Proceed to Secure Checkout - $4,000'}
      </Button>
    </form>
  );
};

const PromotionPayment: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 pt-24 pb-12">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href="/mentorship">Mentorship</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>Promotional Payment</BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Column - Program Details */}
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full mb-4">
                <Clock className="h-4 w-4 mr-1" />
                Limited Time Offer
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Next Generation Medicine Mentorship Program
              </h1>
              <p className="text-xl text-gray-600 mb-6">
                Transform your practice with comprehensive clinical and business training in longevity medicine.
              </p>
            </div>

            {/* Pricing Highlight */}
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="text-2xl text-blue-900">Special Launch Pricing</CardTitle>
                <CardDescription className="text-blue-700">
                  Save $1,000 on your first year
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-4xl font-bold text-blue-900">$4,000</span>
                  <span className="text-lg text-blue-700">first year</span>
                  <span className="text-sm text-gray-500 line-through ml-2">$5,000</span>
                </div>
                <div className="text-sm text-blue-700 mb-4">
                  Then $5,000/year thereafter
                </div>
              </CardContent>
            </Card>

            {/* Program Benefits */}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-gray-900">What's Included</h3>
              <div className="grid gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Comprehensive Lecture Series</h4>
                    <p className="text-gray-600">50+ hours of clinical and business content covering peptides, hormones, longevity protocols, and practice management</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <BookOpen className="h-6 w-6 text-purple-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Clinical Protocols & Templates</h4>
                    <p className="text-gray-600">Ready-to-use treatment protocols, lab interpretation guides, and patient education materials</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Video className="h-6 w-6 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Live Case Review & Business Sessions</h4>
                    <p className="text-gray-600">Weekly group sessions reviewing real patient cases, treatment strategies, and business problem solving</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CreditCard className="h-6 w-6 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Digital Tools Access</h4>
                    <p className="text-gray-600">Basic level access to lab report generator, BI dashboard, and other practice optimization tools</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Star className="h-6 w-6 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Exclusive Member Community</h4>
                    <p className="text-gray-600">Access to private physician network for ongoing collaboration and support</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Payment Form */}
          <div className="lg:sticky lg:top-24">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Secure Checkout</CardTitle>
                <CardDescription>
                  Complete your enrollment in the Next Generation Medicine Mentorship Program
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CheckoutForm />
                
                <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                  <div className="flex items-center justify-center text-sm text-gray-600">
                    <Shield className="h-4 w-4 mr-2" />
                    Secure SSL encrypted payment processing
                  </div>
                  <div className="text-center text-sm text-gray-500">
                    Payment options: Credit/Debit Card or ACH Bank Transfer
                  </div>
                </div>
              </CardContent>
            </Card>


          </div>
        </div>
      </div>
    </div>
  );
};

export default PromotionPayment;